import { useState, useEffect, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { getRememberedWorkspace, clearRememberedWorkspace } from '@/store/authStore';
import { authApi, LoginResponse } from '@/api/authApi';
import { Button } from '@/components/ui/Button';
import { buildPortalSessionUrl } from '@/utils/portalUrl';

type LoginMode = 'activation' | 'returning';

type LoginError = {
  type: 'generic' | 'session_limit' | 'no_password';
  message: string;
  limit?: number;
  retryAfterMinutes?: number;
};

type ElectronActivationState = {
  activated: boolean;
  fingerprintMatch: boolean;
  slug: string;
};

type ElectronBridge = {
  getActivationState?: () => Promise<ElectronActivationState>;
  saveActivation?: (slug: string) => Promise<void>;
  clearActivation?: () => Promise<void>;
};

// Build-time constants — avoid timing issues with zustand hydration
const IS_WEB = import.meta.env.VITE_CLIENT_TYPE === 'Web';
const HAS_GOOGLE_OAUTH = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;

function getElectronAPI(): ElectronBridge | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return !IS_WEB ? (window as any).electronAPI : undefined;
}

function getDeviceCookie(slug: string): boolean {
  try {
    return document.cookie.split(';').some(c => c.trim().startsWith(`wl_device_activated_${slug}=`));
  } catch { return false; }
}

function setDeviceCookie(slug: string) {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `wl_device_activated_${slug}=1; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Strict${secure}`;
  } catch { /* ignore */ }
}

function clearDeviceCookie(slug: string) {
  try {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `wl_device_activated_${slug}=; max-age=0; path=/; SameSite=Strict${secure}`;
  } catch { /* ignore */ }
}

function buildSessionLimitError(data: { limit?: number; message?: string; retryAfterMinutes?: number }): LoginError {
  const retryAfterMinutes = data.retryAfterMinutes ?? 5;
  return {
    type: 'session_limit',
    message: data.message ?? `Maximum active sessions reached. Any idle sessions will clear automatically within ${retryAfterMinutes} minutes. Please try again shortly.`,
    limit: data.limit,
    retryAfterMinutes,
  };
}

// ---------------------------------------------------------------------------
// Floating label input — local to this page, doesn't touch shared Input.tsx
// ---------------------------------------------------------------------------
function FloatInput({
  label, type = 'text', value, onChange, required, autoComplete, autoFocus,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  autoComplete?: string;
  autoFocus?: boolean;
}) {
  const [showPwd, setShowPwd] = useState(false);
  const isPassword = type === 'password';

  return (
    <div className="relative">
      <input
        type={isPassword ? (showPwd ? 'text' : 'password') : type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder=" "
        className="peer block w-full rounded-wl border-2 border-neutral-300 bg-white px-3 py-3.5 text-sm text-on-surface transition-colors focus:border-primary-600 focus:outline-none"
      />
      <label className={`pointer-events-none absolute left-3 px-1 text-neutral-500 transition-all peer-focus:-top-2.5 peer-focus:bg-white peer-focus:text-xs peer-focus:text-primary-600 ${value.length > 0 ? '-top-2.5 bg-white text-xs' : 'top-3.5 bg-transparent text-sm'}`}>
        {label}
      </label>
      {isPassword && (
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShowPwd(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label={showPwd ? 'Hide password' : 'Show password'}
        >
          {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LoginPage() {
  const { login, googleLogin, emailPasswordLogin, tenantPasswordLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const managementAccess = IS_WEB && searchParams.get('management') === '1';
  const requestedDestination = searchParams.get('next');
  const managementDestination = managementAccess && ['/intake', '/inventory', '/work-board', '/appointments'].includes(requestedDestination ?? '')
    ? requestedDestination!
    : '/dashboard';
  const location = useLocation();
  const locationReason = (location.state as { reason?: string } | null)?.reason;
  const clientType = useAuthStore((s) => s.clientType);

  // Web: remembered workspace from localStorage
  const [rememberedSlug, setRememberedSlug] = useState<string | null>(() =>
    IS_WEB ? getRememberedWorkspace() : null
  );

  // Electron: mode + slug from stored activation
  const [mode, setMode] = useState<LoginMode>('activation');
  const [electronSlug, setElectronSlug] = useState('');

  // Activation form state
  const [slug, setSlug] = useState(searchParams.get('slug') ?? '');
  const [code, setCode] = useState('');
  const [activationLoading, setActivationLoading] = useState(false);

  // Web: device activation cookie state
  const [webDeviceActivated, setWebDeviceActivated] = useState<boolean>(() => {
    if (!IS_WEB) return false;
    const initialSlug = searchParams.get('slug') ?? getRememberedWorkspace() ?? '';
    return initialSlug ? getDeviceCookie(initialSlug) : false;
  });

  // Email/password form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [returningLoading, setReturningLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [error, setError] = useState<LoginError | null>(null);

  // Entering Shop Management from the owner portal is an explicit re-authentication
  // boundary. Remove any previously persisted management token before showing credentials.
  useEffect(() => {
    if (!managementAccess) return;
    useAuthStore.getState().clearAuth();
  }, [managementAccess]);

  // Electron: check stored activation on mount
  useEffect(() => {
    if (IS_WEB) return;
    const api = getElectronAPI();
    if (!api?.getActivationState) return;
    api.getActivationState()
      .then((state) => {
        if (state.activated && state.fingerprintMatch) {
          setElectronSlug(state.slug);
          setMode('returning');
        }
      })
      .catch(() => { /* stay on activation */ });
  }, []);

  // Web: re-check device cookie whenever slug changes
  useEffect(() => {
    if (!IS_WEB) return;
    const effectiveSlug = slug || rememberedSlug || '';
    if (effectiveSlug) {
      setWebDeviceActivated(getDeviceCookie(effectiveSlug));
    } else {
      setWebDeviceActivated(false);
    }
  }, [slug, rememberedSlug]);

  const displaySlug = managementAccess ? slug : (IS_WEB ? (rememberedSlug ?? '') : electronSlug);

  async function handleLoginSuccess(response: LoginResponse) {
    const tenantSlug = response.slug;

    if (tenantSlug && response.portalHandoffCode) {
      const portalSessionUrl = buildPortalSessionUrl(tenantSlug, response.portalHandoffCode);
      if (portalSessionUrl) {
        window.location.href = portalSessionUrl;
        return;
      }
    }

    if (response.staffToken && response.staffId != null && response.staffRole && response.staffFullName) {
      useAuthStore.getState().setStaffSession(
        response.staffToken,
        response.staffId,
        response.staffRole,
        response.staffFullName,
      );
      navigate(managementDestination);
      return;
    }

    navigate(managementDestination);
  }

  async function handleActivationSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setActivationLoading(true);
    const trimmedSlug = slug.trim().toLowerCase();
    try {
      try { sessionStorage.setItem('wl.pendingSlug', trimmedSlug); } catch { /* ignore */ }
      const response = await login(trimmedSlug, code.trim());
      try { sessionStorage.removeItem('wl.pendingSlug'); } catch { /* ignore */ }
      if (!response.passwordSet) {
        try { sessionStorage.setItem('wl.showPasswordPrompt', 'true'); } catch { /* ignore */ }
      }
      if (IS_WEB && !managementAccess) {
        setDeviceCookie(trimmedSlug);
        setWebDeviceActivated(true);
      }
      try { await getElectronAPI()?.saveActivation?.(trimmedSlug); } catch { /* ignore */ }
      handleLoginSuccess(response);
    } catch (err: unknown) {
      try { sessionStorage.removeItem('wl.pendingSlug'); } catch { /* ignore */ }
      const ax = err as { response?: { status: number; data?: { error?: string; limit?: number; message?: string; retryAfterMinutes?: number } } };
      const status = ax?.response?.status;
      const data = ax?.response?.data;
      if (status === 403 && data?.error === 'SESSION_LIMIT_REACHED') {
        setError(buildSessionLimitError(data));
      } else if (status === 403 && data?.error === 'TENANT_INACTIVE') {
        setError({ type: 'generic', message: 'This account is not yet active. Contact support if you believe this is an error.' });
      } else if (status === 403 && data?.error === 'BILLING_ISSUE') {
        setError({ type: 'generic', message: 'Access is suspended due to a billing issue. Please contact support.' });
      } else if (status === 401) {
        setError({ type: 'generic', message: 'Invalid slug or onboarding code.' });
      } else if (status) {
        setError({ type: 'generic', message: `Server error (${status}). Please try again or contact support.` });
      } else {
        setError({ type: 'generic', message: 'Could not reach the server. Check your internet connection and try again.' });
      }
    } finally {
      setActivationLoading(false);
    }
  }

  async function handleEmailPasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setReturningLoading(true);
    try {
      const response = managementAccess
        ? await tenantPasswordLogin(slug.trim().toLowerCase(), email.trim(), password)
        : IS_WEB
        ? await authApi.ownerEmailPasswordLogin(email.trim(), password, 'Portal')
        : await emailPasswordLogin(email.trim(), password);
      handleLoginSuccess(response);
    } catch (err: unknown) {
      const ax = err as { response?: { status: number; data?: { error?: string; limit?: number; message?: string; retryAfterMinutes?: number } } };
      const status = ax?.response?.status;
      const data = ax?.response?.data;
      if (status === 404 && data?.error === 'NO_PASSWORD_SET') {
        setError({
          type: 'no_password',
          message: IS_WEB
            ? 'No password set for this account. Use Forgot Password below to create one, or check your welcome email for a setup link.'
            : (data?.message ?? 'No password is set. Use your activation code to sign in, then set a password.'),
        });
      } else if (status === 401) {
        setError({ type: 'generic', message: 'Incorrect email or password.' });
      } else if (status === 403 && data?.error === 'SESSION_LIMIT_REACHED') {
        setError(buildSessionLimitError(data));
      } else if (status === 403 && data?.error === 'TENANT_INACTIVE') {
        setError({ type: 'generic', message: 'This account is not yet active. Contact support if you believe this is an error.' });
      } else if (status === 403 && data?.error === 'BILLING_ISSUE') {
        setError({ type: 'generic', message: 'Access is suspended due to a billing issue. Please contact support.' });
      } else if (status) {
        setError({ type: 'generic', message: `Server error (${status}). Please try again or contact support.` });
      } else {
        setError({ type: 'generic', message: 'Could not reach the server. Check your internet connection and try again.' });
      }
    } finally {
      setReturningLoading(false);
    }
  }

  async function handleGoogleSuccess(credentialResponse: CredentialResponse) {
    const idToken = credentialResponse.credential;
    if (!idToken) return;
    setError(null);
    setGoogleLoading(true);
    try {
      const response = IS_WEB
        ? await authApi.googleLogin(idToken, 'Portal')
        : await googleLogin(idToken);
      handleLoginSuccess(response);
    } catch (err: unknown) {
      const ax = err as { response?: { status: number; data?: { error?: string; limit?: number; message?: string; retryAfterMinutes?: number } } };
      const status = ax?.response?.status;
      const data = ax?.response?.data;
      if (status === 404 && data?.error === 'NO_ACCOUNT_FOUND') {
        setError({ type: 'generic', message: data?.message ?? 'No WrenchLab account found for this Google account. Sign up at wrenchlab.net first.' });
      } else if (status === 403 && data?.error === 'SESSION_LIMIT_REACHED') {
        setError(buildSessionLimitError(data));
      } else if (status === 403 && data?.error === 'TENANT_INACTIVE') {
        setError({ type: 'generic', message: 'This account is not yet active. Contact support if you believe this is an error.' });
      } else if (status === 403 && data?.error === 'BILLING_ISSUE') {
        setError({ type: 'generic', message: 'Access is suspended due to a billing issue. Please contact support.' });
      } else {
        setError({ type: 'generic', message: 'Google sign-in failed. Please try again or use your password.' });
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleElectronReactivate() {
    try { await getElectronAPI()?.clearActivation?.(); } catch { /* ignore */ }
    setElectronSlug('');
    setError(null);
    setMode('activation');
  }

  // Web: always show email+password (no activation code needed). Electron: show if activation complete.
  const showEmailPasswordForm = managementAccess
    ? mode === 'returning'
    : IS_WEB || (!IS_WEB && mode === 'returning');

  return (
    <div className="flex h-screen overflow-hidden">

      {/* ── Left panel: garage photo ── */}
      <div className="hidden lg:block w-3/5 flex-shrink-0">
        <img
          src="/login-bg.png"
          alt=""
          aria-hidden="true"
          className="h-full w-full object-cover object-left"
        />
      </div>

      {/* ── Right panel: form ── */}
      <div className="w-full lg:w-2/5 flex flex-col items-center justify-center bg-white overflow-y-auto px-8 py-12">
        <div className="w-full max-w-[380px]">

          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="WrenchLab" className="h-20 w-auto object-contain" />
          </div>

          {/* Inactivity banner */}
          {locationReason === 'inactivity' && (
            <div className="mb-4 rounded-wl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 flex gap-3">
              <span className="material-symbols-outlined text-blue-500 text-base flex-shrink-0 mt-0.5">timer_off</span>
              <p>You were signed out due to inactivity. Please sign in again.</p>
            </div>
          )}

          {showEmailPasswordForm ? (
            <>
              <h1 className="text-[32px] font-bold text-on-surface mb-1">{managementAccess ? 'Open Shop Management' : (displaySlug ? `Welcome back, ${displaySlug}` : 'Welcome')}</h1>
              <p className="text-sm text-neutral-500 mb-6">{managementAccess ? 'Verify your workspace and owner password to continue.' : 'Sign in with your email and password.'}</p>

              <ErrorBanner error={error} />

              <form onSubmit={handleEmailPasswordSubmit} className="space-y-5">
                {managementAccess && (
                  <FloatInput
                    label="Shop Slug"
                    type="text"
                    value={slug}
                    onChange={e => setSlug(e.target.value.toLowerCase())}
                    required
                    autoComplete="organization"
                  />
                )}
                <FloatInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  autoFocus
                />
                <div>
                  <FloatInput
                    label="Password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <div className="text-right mt-1.5">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs text-neutral-500 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full min-h-[48px] text-sm justify-center"
                  loading={returningLoading}
                >
                  Sign In
                </Button>
              </form>

              {managementAccess && (
                <button type="button" onClick={() => { setError(null); setMode('activation'); }} className="mt-4 w-full text-sm font-semibold text-primary-600 hover:underline">
                  Use onboarding code instead
                </button>
              )}

              <p className="mt-3 text-xs text-neutral-400 text-center">
                By signing in, you agree to WrenchLab's{' '}
                <a href="https://wrenchlab.net/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-500 underline hover:text-neutral-700">Terms of Service</a>
                {' '}and{' '}
                <a href="https://wrenchlab.net/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-500 underline hover:text-neutral-700">Privacy Policy</a>.
              </p>

              {/* Escape hatch — Electron no password set */}
              {!IS_WEB && error?.type === 'no_password' && (
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={() => { setError(null); setMode('activation'); }}
                  >
                    Use Activation Code Instead
                  </Button>
                </div>
              )}

              {/* Google sign-in */}
              {!managementAccess && clientType === 'Web' && HAS_GOOGLE_OAUTH && (
                <>
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-neutral-200" />
                    <span className="text-xs text-neutral-400 font-medium">OR</span>
                    <div className="flex-1 h-px bg-neutral-200" />
                  </div>
                  <div className="flex justify-center">
                    {googleLoading ? (
                      <p className="text-sm text-neutral-500">Signing in with Google…</p>
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => setError({ type: 'generic', message: 'Google sign-in failed. Please try again or use your password.' })}
                        width="100%"
                        text="signin_with"
                        shape="rectangular"
                      />
                    )}
                  </div>
                </>
              )}

              {/* Workspace / portal links */}
              <div className="mt-5 text-center space-y-1.5">
                {IS_WEB && rememberedSlug && (
                  <button
                    type="button"
                    className="block w-full text-sm text-neutral-500 hover:underline"
                    onClick={() => {
                      if (rememberedSlug) clearDeviceCookie(rememberedSlug);
                      clearRememberedWorkspace();
                      setRememberedSlug(null);
                      setSlug('');
                      setWebDeviceActivated(false);
                      setError(null);
                    }}
                  >
                    Not you? Use a different workspace
                  </button>
                )}
                {!IS_WEB && electronSlug && (
                  <button
                    type="button"
                    className="text-sm text-neutral-500 hover:underline"
                    onClick={handleElectronReactivate}
                  >
                    Not your workspace? Re-activate
                  </button>
                )}
              </div>

              {/* Staff workspace login shortcut */}
              {IS_WEB && (
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/workspace-login')}
                    className="text-sm font-semibold text-orange-500 hover:text-orange-600 hover:underline transition-colors"
                  >
                    Staff? Sign in at your shop →
                  </button>
                </div>
              )}

              {/* Divider + footer */}
              <div className="mt-6 border-t border-neutral-200 pt-5 text-center space-y-2">
                <p className="text-xs text-neutral-400">
                  Need help?{' '}
                  <a href="mailto:support@wrenchlab.net" className="text-primary-600 hover:underline">
                    Contact support
                  </a>
                </p>
                <a href="https://wrenchlab.net" className="inline-block text-sm text-neutral-600 hover:underline">
                  ← Back to wrenchlab.net
                </a>
              </div>
            </>
          ) : (
            /* Activation form — first visit (web no cookie) or Electron first launch */
            <>
              <h1 className="text-[32px] font-bold text-on-surface mb-1">{managementAccess ? 'Open Shop Management' : 'Activate your workspace'}</h1>
              <p className="text-sm text-neutral-500 mb-6">
                {managementAccess ? 'Enter the onboarding code issued for this shop.' : 'Enter your shop slug and onboarding code. You only need to do this once per device.'}
              </p>

              <ErrorBanner error={error} />

              <form onSubmit={handleActivationSubmit} className="space-y-5">
                <FloatInput
                  label="Slug"
                  type="text"
                  value={slug}
                  onChange={e => setSlug(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus={!slug}
                />
                <FloatInput
                  label="Onboarding Code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  required
                  autoComplete="current-password"
                  autoFocus={!!slug}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full min-h-[48px] text-sm justify-center"
                  loading={activationLoading}
                >
                  Activate
                </Button>
              </form>

              {managementAccess && (
                <button type="button" onClick={() => { setError(null); setMode('returning'); }} className="mt-4 w-full text-sm font-semibold text-primary-600 hover:underline">
                  Use owner password instead
                </button>
              )}

              <p className="mt-3 text-xs text-neutral-400 text-center">
                By activating, you agree to WrenchLab's{' '}
                <a href="https://wrenchlab.net/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-500 underline hover:text-neutral-700">Terms of Service</a>
                {' '}and{' '}
                <a href="https://wrenchlab.net/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-500 underline hover:text-neutral-700">Privacy Policy</a>.
              </p>

              <div className="mt-6 border-t border-neutral-200 pt-5 text-center space-y-2">
                <p className="text-xs text-neutral-400">
                  Need help?{' '}
                  <a href="mailto:support@wrenchlab.net" className="text-primary-600 hover:underline">
                    Contact support
                  </a>
                </p>
                <a href="https://wrenchlab.net" className="inline-block text-sm text-neutral-600 hover:underline">
                  ← Back to wrenchlab.net
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ErrorBanner({ error }: { error: LoginError | null }) {
  if (!error) return null;
  const colorClass =
    error.type === 'session_limit'
      ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
      : error.type === 'no_password'
      ? 'bg-blue-50 border-blue-200 text-blue-800'
      : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div className={`mb-4 rounded-wl border px-4 py-3 text-sm flex gap-3 ${colorClass}`}>
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
      <div>
        <p>{error.message}</p>
        {error.type === 'session_limit' && (
          <p className="mt-1 text-xs">
            If another device is actively connected, ask the shop owner to revoke it or upgrade the plan.
          </p>
        )}
      </div>
    </div>
  );
}
